/**
 * ForwardMessageDialog — Encaminhar mensagem (Design B: chips + lista).
 *
 * Regras:
 * - Lista apenas contatos compatíveis com o canal da conversa atual
 *   (valida identifier.whatsapp / phone / email / etc via `isContactCompatibleWithProvider`).
 * - Máximo 5 contatos selecionados (enforcement UI + backend).
 * - Busca vai para o backend (server-side search) — não depende de local filter sobre 50 items.
 * - Selecionados aparecem como chips removíveis no topo.
 */

import React, { useMemo, useState } from 'react';
import type { ConversationMessage, ContactIdentifiers } from '@troia-v3/types';
import { Button, Input } from '@/shared/components/ui';
import { Modal, ModalHeader, ModalFooter } from '@/shared/components/ui/Modal';
import { Icon } from '@/shared/icons';
import { isContactCompatibleWithProvider } from '@/shared/utils/channelCompatibility';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useContacts } from '../hooks';

const MAX_CONTACTS = 5;

interface ForwardMessageDialogProps {
  message: ConversationMessage;
  /** channelId da conversa de origem — usado para filtrar contatos compatíveis */
  channelId?: string;
  isOpen: boolean;
  onClose: () => void;
  onForward: (contactIds: string[]) => void;
  isLoading?: boolean;
}

interface ContactRow {
  id: string;
  name: string;
  picture?: string;
  subtitle: string;
  identifiers?: Partial<ContactIdentifiers>;
}

const ForwardMessageDialog: React.FC<ForwardMessageDialogProps> = ({
  message,
  channelId,
  isOpen,
  onClose,
  onForward,
  isLoading = false,
}) => {
  const [selected, setSelected] = useState<Map<string, ContactRow>>(new Map());
  const [searchTerm, setSearchTerm] = useState('');

  // Canal da conversa origem — pega providerId para validar compatibilidade
  const { data: channels } = useChannelsDropdown();
  const currentChannel = channels?.find((c) => c.id === channelId);
  const providerId = currentChannel?.providerId;
  const channelLabel = currentChannel?.name ?? 'Canal atual';

  // Busca server-side (debounce implícito via staleTime do React Query + o usuário espera digitar)
  const { data: contactsData, isLoading: contactsLoading } = useContacts({
    page: 1,
    limit: 100,
    ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
  }, isOpen);

  const contacts = contactsData?.items ?? [];

  // Filtrar contatos por compatibilidade com o canal da conversa origem
  const compatibleContacts = useMemo<ContactRow[]>(() => {
    return contacts
      .filter((c) => isContactCompatibleWithProvider(providerId, c.identifiers))
      .map((c) => {
        const ids: Partial<ContactIdentifiers> = c.identifiers ?? {};
        const subtitle = ids.whatsapp?.[0]
          ?? ids.phone?.[0]
          ?? ids.email?.[0]
          ?? ids.instagram?.[0]
          ?? ids.facebook?.[0]
          ?? ids.telegram?.[0]
          ?? '—';
        return {
          id: c.id,
          name: c.name ?? 'Sem nome',
          ...(c.picture ? { picture: c.picture } : {}),
          subtitle,
          identifiers: ids,
        };
      });
  }, [contacts, providerId]);

  const selectedCount = selected.size;
  const atLimit = selectedCount >= MAX_CONTACTS;

  const toggleContact = (contact: ContactRow) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(contact.id)) {
        next.delete(contact.id);
      } else if (next.size < MAX_CONTACTS) {
        next.set(contact.id, contact);
      }
      return next;
    });
  };

  const removeSelected = (contactId: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(contactId);
      return next;
    });
  };

  const handleForward = () => {
    if (selected.size === 0) return;
    onForward(Array.from(selected.keys()));
  };

  const getMessagePreview = () => {
    const textContent = message.content.find((c) => c.type === 'text' && 'text' in c);
    if (textContent && 'text' in textContent) {
      const text = textContent.text;
      return text.length > 120 ? `${text.substring(0, 120)}…` : text;
    }
    if (message.content.find((c) => c.type === 'image')) return '🖼️ Imagem';
    if (message.content.find((c) => c.type === 'video')) return '🎥 Vídeo';
    if (message.content.find((c) => c.type === 'audio')) return '🎵 Áudio';
    if (message.content.find((c) => c.type === 'document')) return '📄 Documento';
    if (message.content.find((c) => c.type === 'location' as never)) return '📍 Localização';
    return 'Mensagem';
  };

  if (!isOpen) return null;

  // Modal shared (F72): chrome (portal, z-[60], overlay, tema claro fixo,
  // bottom-sheet mobile) vem do componente — só o corpo é custom.
  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md" closeOnEscape={!isLoading}>
      <div data-testid="forward-message-dialog">
        <ModalHeader title="Encaminhar para" onClose={isLoading ? undefined : onClose} />

        {/* Canal de origem — filtra contatos compatíveis */}
        {currentChannel && (
          <div className="px-5 pt-3 inline-flex items-center gap-2 text-xs" style={{ color: '#64748b' }}>
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: currentChannel.status === 'active' ? '#22c55e' : '#cbd5e1' }}
            />
            {channelLabel} · filtrando contatos compatíveis
          </div>
        )}

        {/* Preview da mensagem */}
        <div className="mx-5 my-3 px-3 py-2 rounded-md" style={{ backgroundColor: '#f8fafc', borderLeft: '3px solid #8b5cf6' }}>
          <div className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: '#8b5cf6' }}>
            Mensagem
          </div>
          <div className="text-xs line-clamp-2" style={{ color: '#475569' }}>
            {getMessagePreview()}
          </div>
        </div>

        {/* Chips dos selecionados */}
        <div
          className="mx-5 mb-3 px-2 py-1.5 rounded-lg flex flex-wrap gap-1.5"
          style={{
            backgroundColor: '#fafbfc',
            border: '1px dashed #e5e7eb',
            minHeight: 38,
            alignItems: 'center',
          }}
          data-testid="forward-selected-chips"
        >
          {selectedCount === 0 ? (
            <span className="text-xs px-1" style={{ color: '#94a3b8' }}>
              Selecione até {MAX_CONTACTS} contatos
            </span>
          ) : (
            <>
              {Array.from(selected.values()).map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1.5 text-xs"
                  style={{
                    backgroundColor: '#f5f3ff',
                    color: '#5b21b6',
                    border: '1px solid #ddd6fe',
                    padding: '3px 4px',
                    borderRadius: 16,
                  }}
                  data-testid={`forward-chip-${c.id}`}
                >
                  <span
                    className="flex items-center justify-center text-[10px] font-semibold text-white"
                    style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                    }}
                  >
                    {c.name.substring(0, 2).toUpperCase()}
                  </span>
                  <span className="truncate max-w-[120px]">{c.name}</span>
                  <button
                    onClick={() => removeSelected(c.id)}
                    className="flex items-center justify-center"
                    style={{
                      width: 16, height: 16, borderRadius: '50%',
                      backgroundColor: '#ddd6fe', color: '#5b21b6', cursor: 'pointer',
                    }}
                    data-testid={`forward-chip-remove-${c.id}`}
                  >
                    <Icon name="x" className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              <span className="text-[11px] px-1 self-center" style={{ color: '#94a3b8' }}>
                {selectedCount}/{MAX_CONTACTS}
              </span>
            </>
          )}
        </div>

        {/* Busca */}
        <div className="px-5 mb-2">
          <Input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar contato por nome ou identificador…"
            icon="search"
            data-testid="forward-search-input"
          />
        </div>

        {/* Lista de contatos — scroll interno (o painel do Modal não limita altura no desktop) */}
        <div className="overflow-y-auto max-h-[46vh] sm:max-h-[320px] px-3 pb-2" data-testid="forward-contacts-list">
          {contactsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="loader" className="w-5 h-5 animate-spin" style={{ color: '#8b5cf6' }} />
            </div>
          ) : compatibleContacts.length === 0 ? (
            <div className="text-center py-8 px-4">
              <p className="text-sm" style={{ color: '#64748b' }}>
                {searchTerm
                  ? 'Nenhum contato compatível encontrado'
                  : providerId
                    ? 'Nenhum contato compatível com este canal'
                    : 'Nenhum contato disponível'}
              </p>
            </div>
          ) : (
            compatibleContacts.map((contact) => {
              const isSelected = selected.has(contact.id);
              const isDisabled = !isSelected && atLimit;

              return (
                <button
                  key={contact.id}
                  onClick={() => !isDisabled && toggleContact(contact)}
                  disabled={isDisabled}
                  data-testid={`forward-contact-${contact.id}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !isDisabled) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {contact.picture ? (
                    <img
                      src={contact.picture}
                      alt={contact.name}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #8b5cf6, #a855f7)' }}
                    >
                      {contact.name.substring(0, 2).toUpperCase()}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                      {contact.name}
                    </div>
                    <div className="text-xs truncate" style={{ color: '#94a3b8' }}>
                      {contact.subtitle}
                    </div>
                  </div>

                  <div
                    className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all"
                    style={{
                      borderColor: isSelected ? '#8b5cf6' : '#cbd5e1',
                      backgroundColor: isSelected ? '#8b5cf6' : 'transparent',
                    }}
                  >
                    {isSelected && <Icon name="check" className="w-3 h-3 text-white" />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer — Cancelar secondary (F71) */}
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
            data-testid="forward-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleForward}
            disabled={isLoading || selectedCount === 0}
            loading={isLoading}
            data-testid="forward-submit-btn"
          >
            {isLoading ? (
              'Encaminhando…'
            ) : (
              <>
                <span>Encaminhar para</span>
                <span
                  className="ml-1.5 text-xs font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
                >
                  {selectedCount}
                </span>
              </>
            )}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default ForwardMessageDialog;
